import 'react-app-polyfill/ie11';
import 'react-app-polyfill/stable';
import { ConnectedRouter } from 'connected-react-router';
import React, { Suspense } from 'react';
import Bugsnag from '@bugsnag/js';
import BugsnagPluginReact from '@bugsnag/plugin-react';
import ReactDOM from 'react-dom/client';
import './utils/localization/i18next';
import 'moment/min/locales';
import { Provider } from 'react-redux';
import { LastLocationProvider } from 'react-router-last-location';
import ConditionalWrapper from './components/ConditionalWrapper';
import { BUGSNAG_API_KEY, APP_ENVIRONMENT } from './services/Constants';
import { IS_MOBILE_APP, IS_ANDROID_APP, IS_IOS_APP } from './services/capacitor/Constants';
import { appInfo, handleBrowserOpen, loadAppInfo } from './services/capacitor/Helper';
import { isBootAgent } from './services/Helper';
import { history, store } from './store';
import App from './App';

const RootElement = ReactDOM.createRoot(document.getElementById('root'));

async function startApp() {
	const html = document.getElementsByTagName('html')[0];
	const { version, build } = appInfo || {};
	let keyboardHideTimeout = null;

	window.addEventListener('keyboardDidShow', (KeyboardInfo) => {
		if (keyboardHideTimeout) {
			clearTimeout(keyboardHideTimeout);
			keyboardHideTimeout = null;
		}

		document.documentElement.style.setProperty(
			'--keyboard-height',
			`${KeyboardInfo.keyboardHeight}px`,
		);

		html.classList.add('keyboard-on');
		if (IS_MOBILE_APP) {
			document.activeElement.scrollIntoView({
				behavior: 'smooth',
				block: 'center',
			});
		}
	});

	window.addEventListener('keyboardWillHide', () => {
		html.classList.remove('keyboard-on');
	});

	if (IS_MOBILE_APP) {
		await loadAppInfo();
		await handleBrowserOpen();

		html.classList.add('capacitor');
		if (IS_ANDROID_APP) {
			html.classList.add('capacitor-android');
		} else if (IS_IOS_APP) {
			html.classList.add('capacitor-ios');
			document.body.id = 'scrollable';
		}
	}

	let ErrorBoundary = null;
	if (BUGSNAG_API_KEY !== null && !isBootAgent()) {
		Bugsnag.start({
			apiKey: BUGSNAG_API_KEY,
			plugins: [new BugsnagPluginReact()],
			appVersion: IS_MOBILE_APP
				? `${version} · ${build}`
				: import.meta.env.VITE_APP_JENKINS_BUILD_NUMBER,
			releaseStage: APP_ENVIRONMENT,
		});

		ErrorBoundary = Bugsnag.getPlugin('react').createErrorBoundary(React);
	}

	RootElement.render(
		<ConditionalWrapper
			condition={ErrorBoundary}
			wrapper={(children) => <ErrorBoundary children={children} />}
		>
			<Provider store={store}>
				<ConnectedRouter history={history}>
					<LastLocationProvider>
						<Suspense fallback={<></>}>
							<App />
						</Suspense>
					</LastLocationProvider>
				</ConnectedRouter>
			</Provider>
		</ConditionalWrapper>,
	);
}

startApp().catch((error) => {
	console.error('Failed to start the app:', error);
});
